import { useEffect, useState, useCallback } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { SlidersHorizontal, Map as MapIcon, List, Zap, Sparkles } from "lucide-react";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import { useSeo, EmptyState, Skeleton } from "../components/kit";
import { ServiceInput, LocationInput } from "../components/Inputs";
import ProviderCard from "../components/ProviderCard";
import MapView from "../components/MapView";
import { ProtectedBanner } from "../components/PaymentMode";

export default function SearchPage({ emergencyMode }) {
  const [sp, setSp] = useSearchParams();
  const { cats } = useAuth();
  const [q, setQ] = useState(sp.get("q") || "");
  const [loc, setLoc] = useState(sp.get("location") || "");
  const [f, setF] = useState({ category: sp.get("category") || "", radius: 25, min_rating: 0, verified: false, protected: sp.get("protected") === "1", emergency: emergencyMode || sp.get("emergency") === "1", business_type: "", sort: "best" });
  const [data, setData] = useState(null);
  const [bounds, setBounds] = useState("");
  const [active, setActive] = useState(null);
  const [view, setView] = useState("list");
  const [filters, setFilters] = useState(false);
  const cat = data?.category;
  useSeo(emergencyMode ? "Emergency home services" : `${cat?.name || "Find a Professional"}${loc ? ` in ${loc}` : ""}`, "Search trusted local professionals by service and location on MyHomelyHub.");

  const run = useCallback(async (b = "") => {
    const params = { q, location: loc, ...f, bounds: b };
    const { data } = await api.get("/search/providers", { params });
    setData(data);
  }, [q, loc, f]);

  useEffect(() => { run(); }, [f]); // eslint-disable-line

  const submit = (e) => {
    e.preventDefault();
    setBounds("");
    setSp({ q, location: loc, ...(f.category && { category: f.category }) });
    run();
  };
  const center = data?.center ? [data.center.lat, data.center.lng] : [51.5719, -0.777];
  const set = (k, v) => setF((x) => ({ ...x, [k]: v }));

  return (
    <div className="container-x py-6">
      {emergencyMode && (
        <div className="rounded-2xl bg-rose-50 border border-rose-200 p-4 mb-4 flex items-center gap-3 text-rose-800 text-sm" data-testid="emergency-banner">
          <Zap size={18} /> Showing professionals who offer emergency call-outs, prioritising those available 24/7. In immediate danger (gas leak, fire), call 999 or the National Gas Emergency line first.
        </div>
      )}
      {sp.get("smart") && <div className="rounded-xl bg-[#FFF1EE] text-[#B4462E] text-sm px-4 py-2 mb-4 flex items-center gap-2"><Sparkles size={14} />We understood: “{sp.get("smart")}” · <Link className="underline font-semibold" to={`/post-job?category=${f.category}&q=${encodeURIComponent(sp.get("q") || "")}`}>Post it as a job instead</Link></div>}
      <form onSubmit={submit} className="card p-3 grid gap-2 md:grid-cols-[1fr_1fr_auto_auto]" data-testid="search-form">
        <ServiceInput value={q} onChange={setQ} onPick={(c) => c && set("category", c)} testid="search-service-input" />
        <LocationInput value={loc} onChange={setLoc} testid="search-location-input" />
        <button type="button" onClick={() => setFilters(!filters)} className="btn-ghost" data-testid="search-filters-toggle"><SlidersHorizontal size={16} />Filters</button>
        <button className="btn-coral" data-testid="search-submit-btn">Search</button>
      </form>
      {filters && (
        <div className="card p-4 mt-3 grid grid-cols-2 md:grid-cols-4 lg:grid-cols-7 gap-3 text-sm fade-up" data-testid="search-filters">
          <select className="input" value={f.category} onChange={(e) => set("category", e.target.value)} aria-label="Service" data-testid="filter-category">
            <option value="">All services</option>{cats.map((c) => <option key={c.slug} value={c.slug}>{c.name}</option>)}
          </select>
          <select className="input" value={f.radius} onChange={(e) => set("radius", e.target.value)} aria-label="Distance" data-testid="filter-radius">
            {[5, 10, 15, 25, 50].map((r) => <option key={r} value={r}>Within {r} miles</option>)}
          </select>
          <select className="input" value={f.min_rating} onChange={(e) => set("min_rating", e.target.value)} aria-label="Minimum rating" data-testid="filter-rating">
            {[0, 3, 4, 4.5].map((r) => <option key={r} value={r}>{r ? `${r}+ stars` : "Any rating"}</option>)}
          </select>
          <select className="input" value={f.business_type} onChange={(e) => set("business_type", e.target.value)} aria-label="Business type" data-testid="filter-business-type">
            <option value="">Any business type</option><option>Sole trader</option><option>Limited company</option><option>Partnership</option>
          </select>
          <select className="input" value={f.sort} onChange={(e) => set("sort", e.target.value)} aria-label="Sort" data-testid="filter-sort">
            <option value="best">Best match</option><option value="distance">Nearest</option><option value="rating">Highest rated</option>
          </select>
          <label className="flex items-center gap-2"><input type="checkbox" checked={f.verified} onChange={(e) => set("verified", e.target.checked)} data-testid="filter-verified" />Verified only</label>
          <label className="flex items-center gap-2" title="Pros who take payment through MyHomelyHub: escrow protection and the £1,000 guarantee"><input type="checkbox" checked={f.protected} onChange={(e) => set("protected", e.target.checked)} data-testid="filter-protected" />Protected payment only</label>
          <label className="flex items-center gap-2"><input type="checkbox" checked={f.emergency} onChange={(e) => set("emergency", e.target.checked)} data-testid="filter-emergency" />Emergency service</label>
        </div>
      )}
      <div className="flex items-center justify-between mt-5 mb-3">
        <p className="text-sm text-slate-600" data-testid="search-result-count">{data ? `${data.results.length} professionals${cat ? ` for ${cat.name}` : ""}${data.center?.label ? ` near ${data.center.label}` : ""}` : "Searching…"}</p>
        <div className="lg:hidden inline-flex p-1 bg-slate-100 rounded-lg">
          <button onClick={() => setView("list")} className={`px-3 py-1.5 rounded-md text-xs ${view === "list" ? "bg-white shadow" : ""}`} data-testid="view-list-btn"><List size={14} /></button>
          <button onClick={() => setView("map")} className={`px-3 py-1.5 rounded-md text-xs ${view === "map" ? "bg-white shadow" : ""}`} data-testid="view-map-btn"><MapIcon size={14} /></button>
        </div>
      </div>
      <ProtectedBanner show={f.protected} />
      <div className="grid lg:grid-cols-[minmax(0,1fr)_minmax(0,1fr)] gap-5">
        <div className={`space-y-4 lg:max-h-[calc(100vh-14rem)] lg:overflow-auto lg:pr-2 ${view === "map" ? "hidden lg:block" : ""}`} data-testid="search-results">
          {!data && [1, 2, 3].map((i) => <Skeleton key={i} className="h-44" />)}
          {data?.results.length === 0 && <EmptyState icon={MapIcon} title="No professionals found here yet" text="Try a wider distance or a nearby town — or post your job and let professionals come to you." action={<Link to={`/post-job?category=${f.category}`} className="btn-coral">Post a Job</Link>} />}
          {data?.results.map((p) => <ProviderCard key={p.id} p={p} active={active === p.id} onHover={setActive} />)}
        </div>
        <div className={`lg:sticky lg:top-24 h-[60vh] lg:h-[calc(100vh-14rem)] ${view === "list" ? "hidden lg:block" : ""}`}>
          <div className="relative h-full">
            <MapView items={data?.results || []} center={center} zoom={10} activeId={active} onSelect={setActive} onMove={(b) => setBounds([b.getSouth(), b.getWest(), b.getNorth(), b.getEast()].map((x) => x.toFixed(4)).join(","))} />
            {bounds && <button onClick={() => run(bounds)} className="absolute top-3 left-1/2 -translate-x-1/2 z-[500] btn-navy btn-sm shadow-lg" data-testid="search-this-area-btn">Search this area</button>}
          </div>
        </div>
      </div>
    </div>
  );
}
